import { useState, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Body1,
  Caption1,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  Badge,
  Divider,
} from '@fluentui/react-components';
import { PageTitle } from '@/components/PageTitle';
import {
  ArrowSync24Regular,
  People24Regular,
  Apps24Regular,
  Settings24Regular,
  Info24Regular,
} from '@fluentui/react-icons';
import { useAppContext } from '@/store/appContext';
import { useSharePointList } from '@/hooks/useSharePointList';
import type { Employee } from '@/models/types';
import { canSyncData, canManageRoles } from '@/utils/permissions';
import { AccessDenied } from '@/components/AccessDenied';
import { fetchEntraUsers, syncUsersToSharePoint } from '@/graph/userService';
import {
  fetchSubscribedSkus,
  syncLicensesToSharePoint,
  fetchUserLicenses,
  syncUserLicenseAssignments,
} from '@/graph/licenseService';
import { timeAgo } from '@/utils/formatters';
import { captureDailySnapshot, logChange } from '@/graph/snapshotService';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '24px' },
  section: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
    padding: '24px',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '16px',
    fontWeight: 600,
    fontSize: '16px',
  },
  syncRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    flexWrap: 'wrap' as const,
    gap: '8px',
  },
  roleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    gap: '12px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '120px 1fr',
    gap: '8px',
    padding: '8px 0',
  },
});

export function Settings() {
  const styles = useStyles();
  const { graphClient, currentUser, listIds } = useAppContext();
  const isAdmin = currentUser ? canSyncData(currentUser.role) : false;
  const canRoles = currentUser ? canManageRoles(currentUser.role) : false;

  // Security: Settings page is Admin-only (sync controls, role management)
  if (currentUser && !isAdmin) {
    return <AccessDenied reason="Settings page is restricted to Administrators only." />;
  }

  const { items: employees, refresh: refreshEmployees } =
    useSharePointList<Employee>('OC_Employees', { autoFetch: !!listIds });

  const [syncingUsers, setSyncingUsers] = useState(false);
  const [syncingLicenses, setSyncingLicenses] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [lastUserSync, setLastUserSync] = useState<string | null>(null);
  const [lastLicenseSync, setLastLicenseSync] = useState<string | null>(null);

  const handleUserSync = useCallback(async () => {
    if (!graphClient) return;
    setSyncingUsers(true);
    setMessage(null);

    try {
      const entraUsers = await fetchEntraUsers(graphClient);
      const result = await syncUsersToSharePoint(graphClient, entraUsers);
      const now = new Date().toISOString();
      setLastUserSync(now);
      setMessage({
        type: 'success',
        text: `User sync complete: ${result.created} created, ${result.updated} updated, ${result.deactivated} deactivated`,
      });
      await refreshEmployees();

      // Log change + capture daily snapshot
      await logChange(graphClient, 'Synced', 'Employee', '', 'Entra ID Sync',
        `${result.created} created, ${result.updated} updated, ${result.deactivated} deactivated`,
        currentUser?.email ?? '');
      await captureDailySnapshot(graphClient, currentUser?.email ?? '');
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'User sync failed',
      });
    } finally {
      setSyncingUsers(false);
    }
  }, [graphClient, currentUser, refreshEmployees]);

  const handleLicenseSync = useCallback(async () => {
    if (!graphClient) return;
    setSyncingLicenses(true);
    setMessage(null);

    try {
      const skus = await fetchSubscribedSkus(graphClient);
      const licResult = await syncLicensesToSharePoint(graphClient, skus);

      const userLicenses = await fetchUserLicenses(graphClient);
      const assignmentCount = await syncUserLicenseAssignments(
        graphClient,
        userLicenses,
        skus,
      );

      const now = new Date().toISOString();
      setLastLicenseSync(now);
      setMessage({
        type: 'success',
        text: `License sync complete: ${licResult.skusProcessed} SKUs, ${licResult.appsCreated} new apps, ${assignmentCount} assignments created`,
      });

      // Log change + capture daily snapshot
      await logChange(graphClient, 'Synced', 'License', '', 'Microsoft License Sync',
        `${licResult.skusProcessed} SKUs, ${licResult.appsCreated} new, ${assignmentCount} assignments`,
        currentUser?.email ?? '');
      await captureDailySnapshot(graphClient, currentUser?.email ?? '');
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'License sync failed',
      });
    } finally {
      setSyncingLicenses(false);
    }
  }, [graphClient]);

  // Role management is stored in AD App Roles — this section is informational
  // In a future version, we could store roles in a SharePoint list for more flexibility

  return (
    <div className={styles.page}>
      <PageTitle>Settings</PageTitle>

      {message && (
        <MessageBar intent={message.type}>
          <MessageBarBody>{message.text}</MessageBarBody>
        </MessageBar>
      )}

      {/* Sync Controls */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          <ArrowSync24Regular />
          <Body1>Data Sync</Body1>
        </div>

        <div className={styles.syncRow}>
          <div>
            <Body1 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <People24Regular /> Sync Users from Entra ID
            </Body1>
            <Caption1>
              Pull employee data from Microsoft Entra ID (Azure AD)
            </Caption1>
            {lastUserSync && (
              <Caption1> &middot; Last synced: {timeAgo(lastUserSync)}</Caption1>
            )}
          </div>
          <Button
            appearance="primary"
            icon={syncingUsers ? <Spinner size="tiny" /> : <ArrowSync24Regular />}
            disabled={syncingUsers || !isAdmin}
            onClick={handleUserSync}
          >
            {syncingUsers ? 'Syncing...' : 'Sync Users'}
          </Button>
        </div>

        <div className={styles.syncRow}>
          <div>
            <Body1 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Apps24Regular /> Sync Microsoft Licenses
            </Body1>
            <Caption1>
              Pull subscription and license assignment data from Microsoft Admin
            </Caption1>
            {lastLicenseSync && (
              <Caption1>
                {' '}
                &middot; Last synced: {timeAgo(lastLicenseSync)}
              </Caption1>
            )}
          </div>
          <Button
            appearance="primary"
            icon={
              syncingLicenses ? (
                <Spinner size="tiny" />
              ) : (
                <ArrowSync24Regular />
              )
            }
            disabled={syncingLicenses || !isAdmin}
            onClick={handleLicenseSync}
          >
            {syncingLicenses ? 'Syncing...' : 'Sync Licenses'}
          </Button>
        </div>

        {!isAdmin && (
          <Caption1
            style={{ display: 'block', marginTop: 12, color: tokens.colorNeutralForeground3 }}
          >
            Only admins can sync data. Contact your admin to request a sync.
          </Caption1>
        )}
      </div>

      {/* Role Management */}
      {canRoles && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <Settings24Regular />
            <Body1>Member Roles</Body1>
          </div>
          <Caption1 style={{ display: 'block', marginBottom: 16 }}>
            Roles are managed via Azure AD App Roles. Assign roles in the Azure
            Portal under Enterprise Applications &gt; Prism &gt; Users and
            groups.
          </Caption1>

          <Divider />

          {employees
            .filter((e) => e.fields.Status === 'Active')
            .slice(0, 20)
            .map((emp) => (
              <div key={emp.id} className={styles.roleRow}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Body1>{emp.fields.Title}</Body1>
                  <Caption1>{emp.fields.Email}</Caption1>
                </div>
                <Badge appearance="filled" color="informative" size="small">
                  Member
                </Badge>
              </div>
            ))}

          {employees.filter((e) => e.fields.Status === 'Active').length > 20 && (
            <Caption1 style={{ display: 'block', marginTop: 12 }}>
              Showing first 20 of{' '}
              {employees.filter((e) => e.fields.Status === 'Active').length}{' '}
              active members
            </Caption1>
          )}
        </div>
      )}

      {/* About */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          <Info24Regular />
          <Body1>About</Body1>
        </div>

        <div className={styles.infoGrid}>
          <Caption1>App</Caption1>
          <Body1>Prism v1.0.0</Body1>

          <Caption1>User</Caption1>
          <Body1>{currentUser?.email ?? '—'}</Body1>

          <Caption1>Role</Caption1>
          <Badge
            appearance="filled"
            color={
              currentUser?.role === 'Admin'
                ? 'brand'
                : currentUser?.role === 'RO'
                  ? 'success'
                  : 'informative'
            }
            size="small"
          >
            {currentUser?.role ?? 'Member'}
          </Badge>

          <Caption1>SharePoint</Caption1>
          <Body1>
            {listIds
              ? `${Object.keys(listIds).length} lists connected`
              : 'Not connected'}
          </Body1>

          <Caption1>Environment</Caption1>
          <Body1>{import.meta.env.DEV ? 'Development' : 'Production'}</Body1>
        </div>
      </div>
    </div>
  );
}
